import {
  Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography, useMediaQuery,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@mui/material/styles';
import { BaseButton } from 'components/BaseButton';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: any) => ({
  content: { flex: 1 },
  fullScreenPaper: {
    display: 'flex',
    flexDirection: 'column',
  },
  description: {
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(3),
  },
  title: { color: theme.palette.text.primary },
  actions: {
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    '@media (hover: none) and (pointer: coarse)': {
      '& .MuiButton-root': { minHeight: theme.spacing(5.5) },
    },
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
      paddingBottom: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`,
      '& > *': { flex: 1, minWidth: 0, margin: 0 },
    },
  },
}));

type ConfirmDeletesProps = {
  openModal: boolean;
  isDeleting: boolean;
  handleDeleteTemplate: () => Promise<boolean>;
  closeModal: () => void;
};

export const ConfirmDelete: FC<ConfirmDeletesProps> = ({
  openModal,
  isDeleting,
  handleDeleteTemplate,
  closeModal,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

  const onConfirm = async () => {
    const ok = await handleDeleteTemplate();
    if (ok) closeModal();
  };

  return (
    <Dialog
      open={openModal}
      onClose={() => {
        if (!isDeleting) closeModal();
      }}
      fullScreen={isPhone}
      fullWidth={!isPhone}
      maxWidth="sm"
      classes={isPhone ? { paper: classes.fullScreenPaper } : undefined}
    >
      <DialogTitle style={{ textAlign: 'center' }} className={classes.title}>
        {t('documents:title-delete-template')}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Typography className={classes.description}>
          {t('documents:description-delete-template')}
        </Typography>
        <Box display="flex" justifyContent="space-between" className={classes.actions}>
          <BaseButton
            buttonVariant="outlined"
            buttonColor="inherit"
            icon="times"
            text={t('documents:deny-delete')}
            disabled={isDeleting}
            onClick={() => closeModal()}
          />
          <BaseButton
            buttonVariant="contained"
            backgroundColor={theme.palette.error.main}
            hoverColor={theme.palette.error.dark}
            textColor={theme.palette.error.contrastText}
            icon="trash-alt"
            text={t('documents:confirm-delete')}
            isLoading={isDeleting}
            disabled={isDeleting}
            onClick={onConfirm}
          />
        </Box>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <BaseButton
          buttonVariant="outlined"
          buttonColor="inherit"
          icon="times"
          text={t('common:cancel')}
          disabled={isDeleting}
          onClick={() => closeModal()}
        />
        <BaseButton
          buttonVariant="contained"
          backgroundColor={theme.palette.error.main}
          hoverColor={theme.palette.error.dark}
          textColor={theme.palette.error.contrastText}
          icon="trash"
          text={t('common:delete')}
          isLoading={isDeleting}
          disabled={isDeleting}
          onClick={onConfirm}
        />
      </DialogActions>
    </Dialog>
  );
};